import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseLayout } from 'layouts';
import { useQuests } from '../../helpers/hooks/useQuests';
import { QuestsSummary } from '../../features/quests/QuestsSummary';
import { QuestItem } from '../../features/quests/QuestItem';
import { PointsHistory } from '../../features/quests/PointsHistory';
import { PointsLeaderboard } from '../../features/quests/PointsLeaderboard';
import { Pulse } from '../../features/passive/Pulse';
import { formatPoints } from '../../helpers/quests';

const TABS = ['history', 'leaderboard'];

export const Quests = () => {
  const { t } = useTranslation();
  const { data, isLoading, pendingAction, checkHold, claim } = useQuests();
  const [tab, setTab] = useState(TABS[0]);
  // Обновляем историю и лидерборд после начисления очков
  const refreshKey = data?.pointsBalance;
  const regularQuests = data?.quests?.filter((q) => !q.isGold) ?? [];
  const goldQuests = data?.quests?.filter((q) => q.isGold) ?? [];

  return (
    <BaseLayout className="lg:!bg-[#0C0C0E]">
      <div className="flex flex-col space-y-[2rem] w-full lg:px-[1.6rem] lg:space-y-[1.6rem]">
        <div className="flex flex-col space-y-[0.8rem]">
          <span className="text-[3.2rem] font-600 text-white leading-none lg:text-[2.4rem]">{t('quests.title')}</span>
          <span className="text-[1.4rem] text-white-500">{t('quests.description')}</span>
        </div>

        {isLoading && !data ? (
          <div className="flex flex-col space-y-[1.2rem]">
            {[0, 1, 2].map((i) => (
              <Pulse key={i} className="h-[9rem] w-full !rounded-[2rem]" />
            ))}
          </div>
        ) : (
          <>
            <QuestsSummary data={data} />
            {data?.week ? (
              <>
                <div className="flex flex-col space-y-[1.2rem]">
                  {regularQuests.map((quest) => (
                    <QuestItem
                      key={quest.id}
                      quest={quest}
                      data={data}
                      pendingAction={pendingAction}
                      onClaim={claim}
                      onCheckHold={checkHold}
                    />
                  ))}
                </div>
                {goldQuests.length > 0 && (
                  <div className="flex flex-col space-y-[1.2rem]">
                    <div className="flex flex-col space-y-[0.4rem] pt-[1.2rem]">
                      <span className="text-[2.4rem] font-600 leading-none text-[#FFC24B] lg:text-[2rem]">
                        {t('quests.gold.title')}
                      </span>
                      <span className="text-[1.4rem] text-white-500">{t('quests.gold.subtitle')}</span>
                    </div>
                    <div
                      className={`flex items-center space-x-[1.2rem] p-[1.6rem] rounded-[1.6rem] border-[0.1rem] border-solid text-[1.4rem] ${
                        data.goldUnlocked
                          ? 'border-[rgba(81,246,137,0.35)] bg-[rgba(81,246,137,0.06)] text-green'
                          : 'border-[rgba(255,194,75,0.35)] bg-[rgba(255,194,75,0.06)] text-[#FFC24B]'
                      }`}
                    >
                      <span className="text-[1.8rem] leading-none">{data.goldUnlocked ? '🔓' : '🔒'}</span>
                      <span>
                        {data.goldUnlocked
                          ? t('quests.gold.unlocked')
                          : t('quests.gold.locked', { done: data.completedCount, total: data.coreTotal })}
                      </span>
                    </div>
                    {goldQuests.map((quest) => (
                      <QuestItem
                        key={quest.id}
                        quest={quest}
                        data={data}
                        pendingAction={pendingAction}
                        onClaim={claim}
                        onCheckHold={checkHold}
                      />
                    ))}
                    <div className="flex flex-col space-y-[1rem] p-[2rem] rounded-[2rem] border-[0.1rem] border-dashed border-[rgba(255,194,75,0.4)] bg-[rgba(255,194,75,0.06)]">
                      <div className="flex items-center justify-between text-[1.4rem] lg:flex-col lg:items-start lg:space-y-[0.4rem]">
                        <span className="text-white font-500">
                          {t('quests.grand.title', { bonus: formatPoints(data.grandBonus) })}
                        </span>
                        <span className="text-white-500">
                          {t('quests.grand.progress', { done: data.claimedCount, total: data.quests.length })}
                        </span>
                      </div>
                      <div className="h-[0.6rem] rounded-[2rem] bg-white-100 overflow-hidden">
                        <div
                          className="h-full rounded-[2rem] bg-[#FFC24B]"
                          style={{ width: `${(data.claimedCount / data.quests.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-[2rem] dashboard-block-styles text-[1.4rem] text-white-500">{t('quests.soon')}</div>
            )}
            <div className="flex flex-col dashboard-block-styles p-[0.8rem]">
              <div className="flex space-x-[0.8rem] p-[0.8rem]">
                {TABS.map((key) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`h-[3.6rem] px-[1.6rem] rounded-[1rem] text-[1.4rem] font-500 custom-transition ${
                      tab === key ? 'bg-white-100 text-white' : 'text-white-500 hover:text-white'
                    }`}
                  >
                    {t(`quests.tabs.${key}`)}
                  </button>
                ))}
              </div>
              {tab === 'history' ? (
                <PointsHistory refreshKey={refreshKey} />
              ) : (
                <PointsLeaderboard refreshKey={refreshKey} />
              )}
            </div>
          </>
        )}
      </div>
    </BaseLayout>
  );
};
